import React, { useState, useRef, useEffect } from 'react';
import { Modal, Input, Button, Spin, message, Descriptions, Tag, Divider, Form, Select } from 'antd';
import { v4 as uuid } from 'uuid';
import { EventSourcePolyfill } from '@/pages/chat/chatPanel/util';
import { AgentConfigVo, getAgentConfigInfo, AgentConfigInfo } from '@/api/agent';
import { getTenantList, TenantListVo } from '@/api/auth';

export interface AgentTestChatProp {
  agent: AgentConfigVo;
  onCancel: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  replying?: boolean;
}

const AgentTestChatModal = ({ agent, onCancel }: AgentTestChatProp) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [replying, setReplying] = useState(false);
  const [configInfo, setConfigInfo] = useState<AgentConfigInfo>();
  const [configLoading, setConfigLoading] = useState(true);
  const [tenantList, setTenantList] = useState<TenantListVo[]>([]);
  const [tenantLoading, setTenantLoading] = useState(false);

  // 表单参数
  const [formParams, setFormParams] = useState({
    tenantMcid: '',
    staffId: '',
    cookie: '',
  });

  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const conversationIdRef = useRef<string>(uuid().replace(/-/g, '').slice(0, 32));

  // 加载配置信息（一次性）
  useEffect(() => {
    getAgentConfigInfo(agent.id)
      .then(data => {
        setConfigInfo(data);
      })
      .catch(err => {
        console.error('获取配置信息失败:', err);
        message.error('获取配置信息失败');
      })
      .finally(() => setConfigLoading(false));
  }, [agent.id]);

  // 加载租户列表
  useEffect(() => {
    setTenantLoading(true);
    getTenantList()
      .then(list => setTenantList(list || []))
      .catch(err => {
        console.error('获取租户列表失败:', err);
      })
      .finally(() => setTenantLoading(false));
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight;
      }, 50);
    }
  };

  const handleSend = () => {
    if (!input.trim() || replying) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setReplying(true);

    // 添加 assistant 占位消息
    const assistantMessage: Message = { role: 'assistant', content: '', replying: true };
    setMessages(prev => [...prev, assistantMessage]);

    const config = window.assistantConfig ?? {};
    const apiBase = config.apiUrl || config.baseUrl || '';

    // 使用 streamByAgentConfig 接口（参数与 streamRaw 一致）
    const url = `${apiBase}/api/agent/react/streamByAgentConfig?tenantMcid=${encodeURIComponent(formParams.tenantMcid)}&staffId=${formParams.staffId || ''}&conversationId=${conversationIdRef.current}&message=${encodeURIComponent(input)}&agentConfigId=${agent.id}&cookie=${encodeURIComponent(formParams.cookie)}`;

    let accumulatedContent = '';

    try {
      eventSourceRef.current = new EventSourcePolyfill(url, { method: 'GET' });

      // 监听 REASONING 事件
      eventSourceRef.current.addEventListener('REASONING', (event: MessageEvent) => {
        setLoading(false);
        try {
          const data = JSON.parse(event.data);
          const content = data?.message?.content || [];
          // 提取文本内容
          let text = '';
          if (Array.isArray(content)) {
            for (const block of content) {
              if (block.type === 'text' && block.text) {
                text += block.text;
              }
            }
          }

          // 只在 isLast=true 时更新显示（每轮对话结束时）
          if (data?.isLast === true) {
            accumulatedContent += text;

            setMessages(prev => {
              const newMsgs = [...prev];
              const lastMsg = newMsgs[newMsgs.length - 1];
              if (lastMsg?.role === 'assistant') {
                lastMsg.content = accumulatedContent;
              }
              return newMsgs;
            });
            scrollToBottom();
          }
        } catch (e) {
          console.error('Parse REASONING error:', e);
        }
      });

      // 监听 TOOL_RESULT 事件
      eventSourceRef.current.addEventListener('TOOL_RESULT', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const toolName = data?.message?.name || '工具';
          accumulatedContent += `\n\n[调用工具: ${toolName}]`;

          setMessages(prev => {
            const newMsgs = [...prev];
            const lastMsg = newMsgs[newMsgs.length - 1];
            if (lastMsg?.role === 'assistant') {
              lastMsg.content = accumulatedContent;
            }
            return newMsgs;
          });
          scrollToBottom();
        } catch (e) {
          console.error('Parse TOOL_RESULT error:', e);
        }
      });

      // 监听 done 事件（EventSourcePolyfill 在流结束时发送）
      eventSourceRef.current.addEventListener('done', (event: MessageEvent) => {
        console.log('SSE stream completed (done event)');
        finalizeReply(accumulatedContent || '(无响应)');
      });

      // onerror 处理真正的错误
      eventSourceRef.current.onerror = (err: any) => {
        console.error('SSE Error:', err);
        message.error('连接失败，请检查配置');
        finalizeReply(accumulatedContent || '连接失败');
      };

    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
      setReplying(false);
      message.error('发送失败');
    }
  };

  const finalizeReply = (content: string) => {
    setMessages(prev => {
      const newMsgs = [...prev];
      const lastMsg = newMsgs[newMsgs.length - 1];
      if (lastMsg?.role === 'assistant') {
        lastMsg.content = content || '(无响应)';
        lastMsg.replying = false;
      }
      return newMsgs;
    });
    setLoading(false);
    setReplying(false);

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  const handleStop = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setReplying(false);
    setLoading(false);

    // 标记当前回复停止
    setMessages(prev => {
      const newMsgs = [...prev];
      const lastMsg = newMsgs[newMsgs.length - 1];
      if (lastMsg?.role === 'assistant' && lastMsg.replying) {
        lastMsg.replying = false;
        lastMsg.content = lastMsg.content || '(已停止)';
      }
      return newMsgs;
    });
  };

  return (
    <Modal
      title={`测试智能体: ${agent.name}`}
      open={true}
      onCancel={() => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
        onCancel();
      }}
      footer={null}
      width={700}
    >
      <div style={{ marginBottom: 8, color: '#666' }}>
        智能体编码: {agent.agentCode} | 配置ID: {agent.id} | conversationId: {conversationIdRef.current}
      </div>

      {/* 表单参数配置 */}
      <div style={{ marginBottom: 12, padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>表单参数</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ color: '#666' }}>租户：</label>
            <Select
              placeholder="请选择租户"
              value={formParams.tenantMcid}
              onChange={value => setFormParams(prev => ({ ...prev, tenantMcid: value }))}
              disabled={replying}
              loading={tenantLoading}
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
              options={tenantList.map(t => ({
                value: t.tenantMcid,
                label: t.name,
              }))}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ color: '#666' }}>员工ID：</label>
            <Input
              placeholder="请输入员工ID"
              value={formParams.staffId}
              onChange={e => setFormParams(prev => ({ ...prev, staffId: e.target.value }))}
              disabled={replying}
              style={{ width: 180 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ color: '#666' }}>Cookie：</label>
            <Input
              placeholder="请输入认证Cookie"
              value={formParams.cookie}
              onChange={e => setFormParams(prev => ({ ...prev, cookie: e.target.value }))}
              disabled={replying}
              style={{ width: 260 }}
            />
          </div>
        </div>
      </div>

      {/* 配置信息展示 */}
      {configLoading ? (
        <Spin size="small" style={{ marginBottom: 12 }} />
      ) : configInfo && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" style={{ margin: '8px 0' }}>
            <Tag color="blue">智能体配置</Tag>
          </Divider>
          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="ID">{configInfo.id}</Descriptions.Item>
            <Descriptions.Item label="编码">{configInfo.agentCode}</Descriptions.Item>
            <Descriptions.Item label="名称">{configInfo.name}</Descriptions.Item>
            <Descriptions.Item label="描述">{configInfo.description || '-'}</Descriptions.Item>

            {configInfo.model && (
              <>
                <Descriptions.Item label="模型供应商">
                  <Tag color="green">{configInfo.model.provider}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="模型名称">{configInfo.model.modelName}</Descriptions.Item>
                <Descriptions.Item label="温度">{configInfo.model.temperature}</Descriptions.Item>
                <Descriptions.Item label="Thinking">
                  {configInfo.model.enableThinking ? <Tag color="orange">启用</Tag> : '-'}
                </Descriptions.Item>
              </>
            )}

            {configInfo.prompt && (
              <>
                <Descriptions.Item label="Prompt模板">{configInfo.prompt.templateName}</Descriptions.Item>
                <Descriptions.Item label="版本">{configInfo.prompt.currentVersion}</Descriptions.Item>
                <Descriptions.Item label="模板长度">{configInfo.prompt.sysPromptLength} chars</Descriptions.Item>
              </>
            )}

            {configInfo.skills && configInfo.skills.length > 0 && (
              <>
                <Descriptions.Item label="Skills" span={2}>
                  {configInfo.skills.map(s => (
                    <Tag key={s.skillId} color="purple">{s.skillName} ({s.currentVersion})</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="绑定工具" span={2}>
                  {configInfo.skills.flatMap(s => s.tools || []).map(t => (
                    <Tag key={t} style={{ marginRight: 4 }}>{t}</Tag>
                  ))}
                </Descriptions.Item>
              </>
            )}

            {configInfo.knowledge && (
              <>
                <Descriptions.Item label="知识库">{configInfo.knowledge.kbName}</Descriptions.Item>
                <Descriptions.Item label="RAG模式">{configInfo.knowledge.ragMode}</Descriptions.Item>
                <Descriptions.Item label="TopK">{configInfo.knowledge.ragTopK}</Descriptions.Item>
                <Descriptions.Item label="阈值">{configInfo.knowledge.ragThreshold}</Descriptions.Item>
              </>
            )}

            {configInfo.runtime && (
              <>
                <Descriptions.Item label="最大轮数">{configInfo.runtime.maxIters}</Descriptions.Item>
                <Descriptions.Item label="Plan">
                  {configInfo.runtime.enablePlan ? <Tag color="cyan">启用</Tag> : '-'}
                </Descriptions.Item>
                {configInfo.runtime.hooks && configInfo.runtime.hooks.length > 0 && (
                  <Descriptions.Item label="Hooks" span={2}>
                    {configInfo.runtime.hooks.map(h => (
                      <Tag key={h} color="geekblue">{h}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        </div>
      )}

      {/* 消息区域 */}
      <div
        ref={scrollRef}
        style={{
          height: 300,
          overflowY: 'auto',
          border: '1px solid #eee',
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          backgroundColor: '#fafafa',
        }}
      >
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', paddingTop: 100 }}>
            输入消息开始测试对话
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} style={{ marginBottom: 12 }}>
              {/* 用户消息 */}
              {msg.role === 'user' && (
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      display: 'inline-block',
                      maxWidth: '80%',
                      padding: '8px 12px',
                      borderRadius: 8,
                      backgroundColor: '#1890ff',
                      color: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              )}

              {/* 助手消息 */}
              {msg.role === 'assistant' && (
                <div style={{ textAlign: 'left' }}>
                  <div
                    style={{
                      display: 'inline-block',
                      maxWidth: '80%',
                      padding: '8px 12px',
                      borderRadius: 8,
                      backgroundColor: '#fff',
                      color: '#333',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    {msg.replying && !msg.content && <Spin size="small" />}
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 输入区域 */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          placeholder="输入测试消息..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={handleSend}
          disabled={replying}
        />
        {replying ? (
          <Button danger onClick={handleStop}>停止</Button>
        ) : (
          <Button type="primary" onClick={handleSend} disabled={!input.trim()}>
            发送
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default AgentTestChatModal;